import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { PromptValue } from '@azure/msal-browser';
import { CurrentUserService } from './core/auth/current-user.service';
import { APP_CONFIG } from './core/config/app-config.token';
import { EntitlementService } from './core/entitlements/entitlement.service';
import { toAppError } from './core/http/error-catalog';
import { AnnouncementBannerComponent } from './shared/layout/announcement-banner.component';
import { HeaderComponent } from './shared/layout/header.component';
import { LoginGateComponent } from './shared/layout/login-gate.component';
import { NoAccessComponent } from './shared/layout/no-access.component';
import { SidebarComponent } from './shared/layout/sidebar.component';
import { ShellStore } from './shared/layout/shell.store';
import { ToastService } from './shared/ui/notifications/toast.service';
import { ToasterComponent } from './shared/ui/spartan/sonner.component';

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    AnnouncementBannerComponent,
    HeaderComponent,
    SidebarComponent,
    ToasterComponent,
    LoginGateComponent,
    NoAccessComponent,
  ],
  template: `
    @if (isAuthenticated()) {
      <app-announcement-banner />
      <div class="shell">
        <app-header />
        <app-sidebar [sections]="store.sections()" />
        <main class="shell-content">
          @if (entitlementsError()) {
            <app-no-access
              title="No pudimos cargar tus permisos"
              description="Ocurrió un error al consultar tus accesos. Intenta nuevamente más tarde o cierra la sesión para ingresar con otra cuenta."
              (logout)="logout()"
            />
          } @else if (lacksPermissions()) {
            <app-no-access
              title="No tienes permisos asignados"
              description="Tu cuenta no tiene accesos al Portal de Prestadores. Contacta al administrador de tu institución o cierra la sesión para ingresar con otra cuenta."
              (logout)="logout()"
            />
          } @else {
            <router-outlet />
          }
        </main>
      </div>
    } @else {
      <app-login-gate (continueLogin)="login()" />
    }
    <app-toaster />
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        height: 100dvh;
      }

      .shell {
        display: grid;
        flex: 1;
        min-height: 0;
        grid-template-rows: var(--header-h) 1fr;
        grid-template-columns: var(--sidebar-w) 1fr;
        grid-template-areas:
          'header  header'
          'sidebar content';
        background: var(--bg);
      }

      .shell-content {
        grid-area: content;
        overflow-y: auto;
        padding: 2rem 2.5rem;
        color: var(--text);
        min-height: 0;
      }
    `,
  ],
})
export class AppComponent implements OnInit {
  protected readonly store = inject(ShellStore);
  private readonly msal = inject(MsalService);
  private readonly config = inject(APP_CONFIG);
  private readonly entitlements = inject(EntitlementService);
  private readonly toasts = inject(ToastService);
  private readonly currentUser = inject(CurrentUserService);

  protected readonly isAuthenticated = signal(false);
  protected readonly entitlementsError = signal(false);
  protected readonly lacksPermissions = this.entitlements.lacksPermissions;

  ngOnInit(): void {
    // The redirect response itself is already consumed and the active account already set
    // by the APP_INITIALIZER in app.config.ts, before this component even exists — see the
    // comment there for why. This just reflects that resolved state.
    const account = this.msal.instance.getActiveAccount();
    this.isAuthenticated.set(!!account);
    if (account) {
      this.entitlements.load().subscribe({
        error: (cause: unknown) => {
          this.entitlementsError.set(true);
          this.toasts.showAppError(toAppError(cause));
        },
      });
    }
  }

  protected login(): void {
    this.msal
      .loginRedirect({
        scopes: this.config.auth.msal.apiScopes,
        prompt: PromptValue.SELECT_ACCOUNT,
      })
      .subscribe();
  }

  protected logout(): void {
    this.currentUser.logout();
  }
}
